import { giphyDarkestGrey } from '@giphy/js-brand'
import React, { useContext, useEffect, useState } from 'react'
import styled from 'styled-components'
import { BORDER_RADIUS_MEDIUM, BORDER_RADIUS_SMALL } from '../../../../constants'
import { useCreationSourcePreviewUrl } from '../../../../hooks/use-creation-source-preview-url'
import CreationToolOutputType from '../../../../types/output-type'
import CreationToolStatus from '../../../../types/status'
import { validateSourceByOutput } from '../../../../utils/validate-source'
import ErrorModal from '../../../error-modal'
import EditMenu from '../../edit-menu'
import { CreationToolUploadContext } from '../../manager'
import { CreationToolUploadSource } from '../../types'
import Fields from './fields'
import Header from './header'
import PreviewComponent from './preview'

const Container = styled.div`
    background: ${giphyDarkestGrey};
    border-radius: ${BORDER_RADIUS_SMALL}px;
    margin-bottom: 5px;
    min-height: 50px;
    position: relative;
`

const Error = styled(ErrorModal)`
    background: rgba(0, 0, 0, 0.9);
`

const Preview = styled(PreviewComponent)`
    border-radius: ${BORDER_RADIUS_MEDIUM}px;
    margin-top: 5px;
`

type Props = {
    source: CreationToolUploadSource
    isPreviewingByDefault?: boolean
}

const CreationUploadItem = ({ source, isPreviewingByDefault = false }: Props) => {
    const { source: src } = source
    const { status: uploadStatus, uploadSources, removeUploadSource, updateUploadSource, editing } = useContext(
        CreationToolUploadContext
    )
    const [isPreviewing, setIsPreviewing] = useState<boolean>(isPreviewingByDefault)
    const previewUrl = useCreationSourcePreviewUrl(src.source)
    const { error, status, id: sourceId, uploadedId } = source
    const isUploading = uploadStatus !== CreationToolStatus.Idle
    const isBulk = uploadSources.length > 1
    const isEditable = CreationToolOutputType.editable(source.outputType)

    useEffect(() => {
        if (uploadStatus !== CreationToolStatus.Idle) {
            setIsPreviewing(false)
        }
    }, [uploadStatus, setIsPreviewing])

    const onChangeOutputType = async (outputType: CreationToolOutputType) => {
        const validatedFile = await validateSourceByOutput(src.source, outputType)
        updateUploadSource({
            ...source,
            source: validatedFile,
            error: undefined,
            outputType,
        })
    }

    const onRetry = () => {
        updateUploadSource({
            ...source,
            error: undefined,
        })
    }

    return (
        <Container
            style={
                isUploading && status === CreationToolStatus.Idle && !uploadedId
                    ? { opacity: 0.5, pointerEvents: 'none' }
                    : {}
            }
        >
            <Header
                {...{
                    source,
                    isBulk,
                    isPreviewing,
                    isUploading,
                    setIsPreviewing,
                }}
            />
            {isPreviewing && !editing && (
                <>
                    <Preview source={source} previewUrl={previewUrl}>
                        {error ? (
                            <Error
                                {...{
                                    error,
                                    sourceId: src.id,
                                    stickerDisplayMode: 'small',
                                    goBackLabel: `Remove`,
                                    onGoBack: () => removeUploadSource(sourceId),
                                    onChangeOutputType,
                                    onRetry,
                                }}
                            />
                        ) : (
                            isEditable && <EditMenu source={source} setIsPreviewing={setIsPreviewing} />
                        )}
                    </Preview>
                    {!error && (
                        <Fields
                            {...{
                                source,
                                isBulk,
                            }}
                        />
                    )}
                </>
            )}
        </Container>
    )
}

export default CreationUploadItem
